/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { disablePictureInPicture, enablePictureInPicture, videoDeviceDisabled } from '../../actions';
import { CLEAN_UP, MERGED_STREAM_UPDATED, PICTURE_IN_PICTURE_DISABLED, PICTURE_IN_PICTURE_ENABLED, SCREEN_SHARING_DISABLED, SCREEN_SHARING_ENABLED, SCREEN_SHARING_ENDED, SCREEN_SHARING_TYPE_CHANGED, SHOW_PIP_MODAL, STARTOVER_CLICKED, STOP_MERGED_STREAM, UNMOUNT_CLEAN_UP } from '../../constants/ActionTypes';
import { DeviceError } from '../../constants/DeviceError';
let pipVideoElement;
let askedForPip = false;
async function openPictureInPictureWindow(dispatch, getState) {
  pipVideoElement = document.createElement('video');
  pipVideoElement.addEventListener('loadedmetadata', async () => {
    pipVideoElement.play();
    pipVideoElement.muted = true;
    pipVideoElement.requestPictureInPicture();
  });
  pipVideoElement.addEventListener('leavepictureinpicture', () => {
    const _getState = getState(),
      captureState = _getState.captureState,
      pipEnabled = _getState.pipEnabled,
      videoDeviceId = _getState.videoDeviceId,
      screenSharingActive = _getState.screenSharingActive;
    if (pipEnabled) {
      dispatch(disablePictureInPicture());
      pipVideoElement.srcObject = null;
      pipVideoElement.load();
    }
    if (captureState === 'READY' && videoDeviceId !== null && screenSharingActive) {
      dispatch(videoDeviceDisabled());
    }
  });
  const _getState2 = getState(),
    streamMerger = _getState2.streamMerger;
  pipVideoElement.srcObject = streamMerger.webcamStream;
}
function closePictureInPictureWindow() {
  if (document.pictureInPictureElement) {
    document.exitPictureInPicture();
  }
}
export default function pictureInPictureMiddleware({
  dispatch,
  getState
}) {
  return next => action => {
    switch (action.type) {
      case MERGED_STREAM_UPDATED:
        {
          const _getState3 = getState(),
            pipEnabled = _getState3.pipEnabled,
            streamMerger = _getState3.streamMerger,
            screenSharingActive = _getState3.screenSharingActive,
            screenSharingType = _getState3.screenSharingType,
            noSystemPermissionDevices = _getState3.noSystemPermissionDevices;
          if (!screenSharingActive || !streamMerger.getStream()) {
            break;
          }
          if (screenSharingType === 'monitor') {
            if (streamMerger.webcamStream && !pipEnabled) {
              // we have full screen screen share and webcam, we need to enable pip
              dispatch(enablePictureInPicture());
            } else if (!streamMerger.webcamStream && pipEnabled) {
              // we have full screen screen share but no webcam, we need to close pip window
              dispatch(disablePictureInPicture());
            } else if (streamMerger.webcamStream && pipEnabled) {
              // just update the pip window with the new webcam stream
              pipVideoElement.srcObject = streamMerger.webcamStream;
            } else if (!streamMerger.webcamStream && !noSystemPermissionDevices.has(DeviceError.Webcam) && !pipEnabled && !askedForPip) {
              // we have full screen screen share but no webcam, we ask the user if they want to enable pip
              dispatch({
                type: SHOW_PIP_MODAL
              });
              askedForPip = true;
            }
          }
          break;
        }
      case SCREEN_SHARING_ENABLED:
        {
          dispatch(videoDeviceDisabled());
          askedForPip = false;
          break;
        }
      case PICTURE_IN_PICTURE_ENABLED:
        {
          openPictureInPictureWindow(dispatch, getState);
          break;
        }
      case PICTURE_IN_PICTURE_DISABLED:
        {
          closePictureInPictureWindow();
          break;
        }
      case STARTOVER_CLICKED:
        {
          askedForPip = false;
          break;
        }
      case SCREEN_SHARING_TYPE_CHANGED:
        {
          const _getState4 = getState(),
            screenSharingType = _getState4.screenSharingType,
            pipEnabled = _getState4.pipEnabled;
          if (pipEnabled && screenSharingType !== 'monitor') {
            closePictureInPictureWindow();
          }
          break;
        }
      case CLEAN_UP:
      case SCREEN_SHARING_ENDED:
      case SCREEN_SHARING_DISABLED:
      case STOP_MERGED_STREAM:
      case UNMOUNT_CLEAN_UP:
        {
          closePictureInPictureWindow();
          askedForPip = false;
          break;
        }
      default:
        break;
    }
    return next(action);
  };
}